"use client";

import { useState } from "react";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { graphemeLength, segmentGraphemes } from "@/lib/graphemes";
import { tPlural } from "@/lib/i18n/interpolate";

const WORD_SPLIT_RE = /[\s,;]+/;
const LETTERS_ONLY_RE = /^(?:\p{L}\p{M}*)+$/u;
const MIN_WORDS = 3;
const MAX_WORDS = 20;
const MAX_WORD_LENGTH = 15;
const PLACE_ATTEMPTS = 300;
const GRID_SIZES = [10, 12, 14, 16, 18, 20];

export interface Direction {
  dr: number;
  dc: number;
}

export interface Puzzle {
  size: number;
  grid: string[][];
  answerCells: Set<string>;
  placedWords: string[];
  failedWords: string[];
}

export function parseWords(input: string, locale?: string): string[] {
  return input
    .split(WORD_SPLIT_RE)
    .map((word) => word.trim().normalize("NFC").toLocaleUpperCase(locale))
    .filter((w) => w.length > 0);
}

export function isUnicodeWord(word: string): boolean {
  return LETTERS_ONLY_RE.test(word.normalize("NFC"));
}

function validateWords(
  words: string[],
  strings: Strings,
  locale?: string
): string[] {
  const errors: string[] = [];
  if (words.length < MIN_WORDS) {
    errors.push(strings.minWordsError.replace("{min}", String(MIN_WORDS)));
  }
  if (words.length > MAX_WORDS) {
    errors.push(strings.maxWordsError.replace("{max}", String(MAX_WORDS)));
  }
  const invalid = words.filter((w) => !isUnicodeWord(w));
  if (invalid.length > 0) {
    errors.push(
      strings.lettersOnlyError.replace(
        "{words}",
        invalid.slice(0, 5).join(", ")
      )
    );
  }
  const tooLong = words.filter(
    (word) => graphemeLength(word, locale) > MAX_WORD_LENGTH
  );
  if (tooLong.length > 0) {
    errors.push(
      strings.maxLengthError
        .replace("{maxLen}", String(MAX_WORD_LENGTH))
        .replace("{words}", tooLong.slice(0, 5).join(", "))
    );
  }
  return errors;
}

function buildDirections(
  horizontal: boolean,
  vertical: boolean,
  diagonal: boolean,
  reversed: boolean
): Direction[] {
  const dirs: Direction[] = [];
  if (horizontal) {
    dirs.push({ dr: 0, dc: 1 });
  }
  if (vertical) {
    dirs.push({ dr: 1, dc: 0 });
  }
  if (diagonal) {
    dirs.push({ dr: 1, dc: 1 });
    dirs.push({ dr: 1, dc: -1 });
  }
  if (reversed) {
    for (const d of dirs.slice()) {
      dirs.push({ dr: -d.dr, dc: -d.dc });
    }
  }
  return dirs;
}

function randomInt(max: number, random: () => number): number {
  return Math.floor(random() * max);
}

function canPlace(
  grid: string[][],
  word: string,
  row: number,
  col: number,
  dir: Direction,
  locale: string | undefined,
  collator: Intl.Collator
): boolean {
  const size = grid.length;
  const letters = segmentGraphemes(word, locale);
  const endRow = row + dir.dr * (letters.length - 1);
  const endCol = col + dir.dc * (letters.length - 1);
  if (endRow < 0 || endRow >= size || endCol < 0 || endCol >= size) {
    return false;
  }
  for (let i = 0; i < letters.length; i += 1) {
    const cell = grid[row + dir.dr * i]?.[col + dir.dc * i] ?? "";
    const letter = letters[i] ?? "";
    if (cell !== "" && collator.compare(cell, letter) !== 0) {
      return false;
    }
  }
  return true;
}

function writeWord(
  grid: string[][],
  word: string,
  row: number,
  col: number,
  dir: Direction,
  answerCells: Set<string>,
  locale?: string
): void {
  const letters = segmentGraphemes(word, locale);
  for (let i = 0; i < letters.length; i += 1) {
    const r = row + dir.dr * i;
    const c = col + dir.dc * i;
    const gridRow = grid[r];
    if (gridRow) {
      gridRow[c] = letters[i] ?? "";
    }
    answerCells.add(`${r}-${c}`);
  }
}

function tryPlaceWord(
  grid: string[][],
  word: string,
  dirs: Direction[],
  answerCells: Set<string>,
  locale: string | undefined,
  collator: Intl.Collator,
  random: () => number
): boolean {
  const size = grid.length;
  for (let attempt = 0; attempt < PLACE_ATTEMPTS; attempt += 1) {
    const dir = dirs[randomInt(dirs.length, random)];
    if (!dir) {
      return false;
    }
    const row = randomInt(size, random);
    const col = randomInt(size, random);
    if (canPlace(grid, word, row, col, dir, locale, collator)) {
      writeWord(grid, word, row, col, dir, answerCells, locale);
      return true;
    }
  }
  return false;
}

function fillEmptyCells(
  grid: string[][],
  words: string[],
  locale: string | undefined,
  random: () => number
): void {
  const enteredGraphemes = words.flatMap((word) =>
    segmentGraphemes(word, locale)
  );
  const fillerGraphemes =
    enteredGraphemes.length > 0 ? enteredGraphemes : ["A"];
  for (const row of grid) {
    for (let c = 0; c < row.length; c += 1) {
      if (row[c] === "") {
        row[c] =
          fillerGraphemes[randomInt(fillerGraphemes.length, random)] ?? "A";
      }
    }
  }
}

export function generatePuzzle(
  words: string[],
  size: number,
  dirs: Direction[],
  locale?: string,
  random: () => number = Math.random
): Puzzle {
  const grid: string[][] = Array.from({ length: size }, () =>
    Array.from({ length: size }, () => "")
  );
  const answerCells = new Set<string>();
  const placedWords: string[] = [];
  const failedWords: string[] = [];
  const collator = new Intl.Collator(locale, {
    sensitivity: "variant",
    usage: "search",
  });
  const ordered = words
    .slice()
    .sort(
      (a, b) =>
        graphemeLength(b, locale) - graphemeLength(a, locale) ||
        collator.compare(a, b)
    );
  for (const word of ordered) {
    if (tryPlaceWord(grid, word, dirs, answerCells, locale, collator, random)) {
      placedWords.push(word);
    } else {
      failedWords.push(word);
    }
  }
  fillEmptyCells(grid, words, locale, random);
  return { size, grid, answerCells, placedWords, failedWords };
}

/** English strings. A translated content file overrides these through `ui`. */
export const WORD_SEARCH_GENERATOR_STRINGS = {
  wordsLabel:
    "Words — one per line or comma-separated ({min}–{max} words, letters only, max {maxLen} letters)",
  inputPlaceholder: "PHOTOSYNTHESIS\nCHLOROPLAST\nGLUCOSE\nOXYGEN\nSUNLIGHT",
  word: "one:word|other:words",
  gridSize: "Grid size",
  horizontal: "Horizontal",
  vertical: "Vertical",
  diagonal: "Diagonal",
  allowReversed: "Allow reversed",
  regenerate: "Regenerate",
  generate: "Generate",
  hideAnswerKey: "Hide answer key",
  showAnswerKey: "Show answer key",
  print: "Print",
  didntFit:
    "Didn't fit: {words}. Try a larger grid, more directions, or Regenerate.",
  findTheseWords: "Find these words",
  minWordsError: "Enter at least {min} words.",
  maxWordsError: "Enter at most {max} words.",
  lettersOnlyError: "Letters only — remove: {words}",
  maxLengthError: "Max {maxLen} letters per word — shorten: {words}",
  tooLongForGridError: "Longer than the {size}x{size} grid: {words}",
  noDirectionError: "Enable at least one direction.",
} as const;

type Strings = typeof WORD_SEARCH_GENERATOR_STRINGS;

export default function WordSearchGeneratorWidget() {
  const locale = useToolLocale();
  const strings = useToolUi(WORD_SEARCH_GENERATOR_STRINGS);
  const [input, setInput] = useState("");
  const [gridSize, setGridSize] = useState(12);
  const [horizontal, setHorizontal] = useState(true);
  const [vertical, setVertical] = useState(true);
  const [diagonal, setDiagonal] = useState(false);
  const [reversed, setReversed] = useState(false);
  const [showKey, setShowKey] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [puzzle, setPuzzle] = useState<Puzzle | null>(null);

  const words = parseWords(input, locale);
  const noDirections = !(horizontal || vertical || diagonal);

  function handleGenerate() {
    const validationErrors = validateWords(words, strings, locale);
    const tooBig = words.filter(
      (word) => graphemeLength(word, locale) > gridSize
    );
    if (tooBig.length > 0) {
      validationErrors.push(
        strings.tooLongForGridError
          .replaceAll("{size}", String(gridSize))
          .replace("{words}", tooBig.slice(0, 5).join(", "))
      );
    }
    if (noDirections) {
      validationErrors.push(strings.noDirectionError);
    }
    setErrors(validationErrors);
    if (validationErrors.length > 0) {
      setPuzzle(null);
      return;
    }
    const dirs = buildDirections(horizontal, vertical, diagonal, reversed);
    setPuzzle(generatePuzzle(words, gridSize, dirs, locale));
  }

  return (
    <>
      <style>{"@media print { header, footer, nav { display: none } }"}</style>

      {/* Word list */}
      <div className="space-y-2 print:hidden">
        <label
          className="font-medium text-foreground text-sm"
          htmlFor="word-input"
        >
          {strings.wordsLabel
            .replace("{min}", String(MIN_WORDS))
            .replace("{max}", String(MAX_WORDS))
            .replace("{maxLen}", String(MAX_WORD_LENGTH))}
        </label>
        <textarea
          className="min-h-[160px] w-full resize-y rounded-lg border border-border bg-card p-4 font-mono text-foreground text-sm leading-relaxed placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary"
          id="word-input"
          onChange={(e) => setInput(e.target.value)}
          placeholder={strings.inputPlaceholder}
          value={input}
        />
        <p className="text-muted-foreground text-xs">
          {words.length} {tPlural(strings.word, locale, words.length)}
        </p>
      </div>

      {/* Options */}
      <section className="space-y-4 print:hidden">
        <div className="flex flex-wrap items-center gap-3">
          <label
            className="font-medium text-foreground text-sm"
            htmlFor="grid-size"
          >
            {strings.gridSize}
          </label>
          <select
            className="rounded-lg border border-border bg-card px-3 py-2 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
            id="grid-size"
            onChange={(e) => setGridSize(Number(e.target.value))}
            value={gridSize}
          >
            {GRID_SIZES.map((s) => (
              <option key={s} value={s}>
                {s} × {s}
              </option>
            ))}
          </select>
        </div>
        <div className="flex flex-wrap gap-3">
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
            <input
              checked={horizontal}
              className="accent-primary"
              onChange={(e) => setHorizontal(e.target.checked)}
              type="checkbox"
            />
            {strings.horizontal}
          </label>
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
            <input
              checked={vertical}
              className="accent-primary"
              onChange={(e) => setVertical(e.target.checked)}
              type="checkbox"
            />
            {strings.vertical}
          </label>
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
            <input
              checked={diagonal}
              className="accent-primary"
              onChange={(e) => setDiagonal(e.target.checked)}
              type="checkbox"
            />
            {strings.diagonal}
          </label>
          <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-border bg-card px-4 py-2 text-sm transition-colors has-[:checked]:border-primary has-[:checked]:bg-primary/5">
            <input
              checked={reversed}
              className="accent-primary"
              onChange={(e) => setReversed(e.target.checked)}
              type="checkbox"
            />
            {strings.allowReversed}
          </label>
        </div>
      </section>

      {/* Actions */}
      <div className="flex flex-wrap gap-3 print:hidden">
        <button
          className="rounded-lg bg-primary px-5 py-2 font-medium text-primary-foreground text-sm transition-opacity hover:opacity-90 disabled:opacity-40"
          disabled={words.length === 0}
          onClick={handleGenerate}
          type="button"
        >
          {puzzle ? strings.regenerate : strings.generate}
        </button>
        {puzzle && (
          <>
            <button
              className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted"
              onClick={() => setShowKey((prev) => !prev)}
              type="button"
            >
              {showKey ? strings.hideAnswerKey : strings.showAnswerKey}
            </button>
            <button
              className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted"
              onClick={() => window.print()}
              type="button"
            >
              {strings.print}
            </button>
          </>
        )}
      </div>

      {/* Errors */}
      {errors.length > 0 && (
        <div className="space-y-1 rounded-lg border border-destructive/40 bg-destructive/5 p-4 print:hidden">
          {errors.map((err) => (
            <p className="text-destructive text-sm" key={err}>
              {err}
            </p>
          ))}
        </div>
      )}

      {/* Puzzle (printable) */}
      {puzzle && (
        <section className="space-y-4">
          {puzzle.failedWords.length > 0 && (
            <p className="rounded-lg border border-border bg-muted/30 p-4 text-muted-foreground text-sm print:hidden">
              {strings.didntFit.replace(
                "{words}",
                puzzle.failedWords.join(", ")
              )}
            </p>
          )}
          <div className="overflow-x-auto">
            <table className="mx-auto border-collapse font-mono">
              <tbody>
                {puzzle.grid.map((row, r) => (
                  <tr key={`row-${r + 1}`}>
                    {row.map((letter, c) => (
                      <td
                        className={`h-7 w-7 border border-border text-center text-foreground text-sm ${showKey && puzzle.answerCells.has(`${r}-${c}`) ? "bg-primary/20 font-bold" : ""}`}
                        key={`cell-${r + 1}-${c + 1}`}
                      >
                        {letter}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="space-y-2">
            <h2 className="font-semibold text-foreground text-lg">
              {strings.findTheseWords}
            </h2>
            <ul className="grid grid-cols-2 gap-x-6 gap-y-1 sm:grid-cols-3">
              {puzzle.placedWords
                .slice()
                .sort((a, b) => a.localeCompare(b, locale))
                .map((word) => (
                  <li
                    className="font-mono text-muted-foreground text-sm print:text-foreground"
                    key={word}
                  >
                    {word}
                  </li>
                ))}
            </ul>
          </div>
        </section>
      )}
    </>
  );
}
